import { InterruptedRecordingStore } from './interrupted_recording_store.js'
import {
  formatDurationLong,
  isPendingResumeEligible,
} from './interrupted_recording_helpers.js'

// A composer can disconnect and reconnect while its IndexedDB lookup or label
// import is pending. Only the newest manager for its template may offer a toast.
const currentManagers = new WeakMap()

export class InterruptedRecordingManager {
  constructor({
    key,
    toastTemplate,
    onRestore,
    store = InterruptedRecordingStore,
    loadLabels = () => import('./interrupted_recording_labels.js'),
  }) {
    this.key = key
    this.toastTemplate = toastTemplate
    this.onRestore = onRestore
    this.store = store
    this.loadLabels = loadLabels
    this.sessionId = null
    this.toastEl = null
  }

  #isCurrent() {
    return (
      this.toastTemplate?.isConnected !== false &&
      currentManagers.get(this.toastTemplate) === this
    )
  }

  async connect() {
    if (!this.key || !this.toastTemplate) return
    currentManagers.set(this.toastTemplate, this)

    try {
      await this.store.purgeExpired()
      if (!this.#isCurrent()) return
      await this.store.enforceCap()
      if (!this.#isCurrent()) return

      // Only the most recent recording is offered; if it's already been restored or
      // dismissed, stay quiet rather than digging up an older one.
      const session = await this.store.latestForKey(this.key)
      if (!session || !this.#isCurrent()) return

      this.sessionId = session.sessionId

      // Mid-transcription interruptions (esp. iOS lock while submit is waiting on
      // the batch transcribe) should pick up silently — no "failed" toast, no
      // Restore click. Looks like it just continued when the phone unlocked.
      if (isPendingResumeEligible(session)) {
        await this.#autoResume(session)
        return
      }

      if (session.shown) return

      await this.#showToast(session)
    } catch (error) {
      console.error('Interrupted recording recovery failed:', error)
    }
  }

  async #autoResume(session) {
    const blob = await this.store.getBlob(session.sessionId)
    if (!this.#isCurrent()) return
    if (!blob) {
      // Nothing to restore — clear the flag so we don't loop, and fall back to toast
      // if the session is still offerable.
      await this.store.clearPendingResume(session.sessionId)
      if (!session.shown && this.#isCurrent()) await this.#showToast(session)
      return
    }

    // Don't markShown yet — if auto-resume dies again we still want another try
    // (or the toast) on the next open. pendingResume stays until transcription
    // succeeds (cleared by the consumer).
    await this.onRestore?.(session.sessionId, blob, {
      autoSubmit: !!session.autoSubmit,
      silent: true,
    })
  }

  async #showToast({ durationMs, startedAt }) {
    const { dateLabel } = await this.loadLabels()
    if (!this.#isCurrent()) return
    const toast = this.toastTemplate.content.cloneNode(true)
    const { day, time } = dateLabel(startedAt)
    toast.querySelector(
      '.js-restore-toast-text'
    ).textContent = `Your recording (${formatDurationLong(
      durationMs
    )} long) from ${day} ${time} was interrupted.`

    this.toastEl = toast.firstElementChild

    // Dismissing the toast means "don't offer this recording again".
    this.toastEl.addEventListener('toast:dismissed', () =>
      this.store.markShown(this.sessionId)
    )

    toast
      .querySelector('.js-restore-recording')
      ?.addEventListener('click', (event) => {
        event.stopPropagation() // restoring isn't a dismissal — don't mark it shown
        this.#restore()
      })
    // 'View all' (in the toast template) links to the interrupted-recordings modal.

    document.getElementById('toasts')?.appendChild(toast)
  }

  async #restore() {
    this.toastEl?.remove()
    this.toastEl = null
    await this.store.markShown(this.sessionId)
    const blob = await this.store.getBlob(this.sessionId)
    if (blob) await this.onRestore(this.sessionId, blob, { silent: false })
  }
}
